import {H2} from '@jetbrains/ring-ui/components/heading/heading'
import plur from 'plur'
import * as React from 'react'

import BuildLineLayout from '../../../../../containers/BuildLineLayout'
import type {AgentPoolId} from '../../../../../types'
import {internalProps} from '../../../../../types/BS_types'
import {sortSameChildren} from '../../AgentsPages.utils'
import type {AgentsTreeAgentPoolNode} from '../../AgentsSidebar/AgentsSidebar.types'
import {isAgentNode, isAgentTypeNode} from '../../AgentsSidebar/AgentsSidebar.utils'
import AgentsGroup from '../../components/AgentsGroup/AgentsGroup'
import AgentsSortingSelect, {
  useAgentsSortingSelectState,
} from '../../components/AgentsSortingSelect/AgentsSortingSelect'
import CloudAgentRow from '../../components/CloudAgentRow/CloudAgentRow'
import AgentPoolTabToolbar from '../AgentPoolTabToolbar/AgentPoolTabToolbar'

import AssignAgentsButton from './AssignAgentsButton/AssignAgentsButton'

import styles from './AgentsTab.css'

type Props = {
  agentPoolTree: AgentsTreeAgentPoolNode
  agentPoolId: AgentPoolId
  maxAgentsNumber: number | null
  canManageAgents: boolean
  canRemoveAgents: boolean
}

function AgentsTab({
  agentPoolTree,
  agentPoolId,
  maxAgentsNumber,
  canManageAgents,
  canRemoveAgents,
}: Props): React.ReactElement {
  const cloudAgentNodes = agentPoolTree.children.filter(isAgentTypeNode)
  const standAloneAgentNodes = agentPoolTree.children.filter(isAgentNode)
  const cloudAgentsNumber = cloudAgentNodes.length
  const standAloneAgentsNumber = standAloneAgentNodes.length
  const agentsNumber = agentPoolTree.count
  const [agentsSorting, setAgentsSorting] = useAgentsSortingSelectState()
  const isEmpty = cloudAgentsNumber === 0 && agentsNumber === 0
  const showAssignAgentsButton =
    internalProps['teamcity.ui.newAgentPoolManagement'] !== false && canManageAgents
  const showAgentsToolbar =
    showAssignAgentsButton || isEmpty || (maxAgentsNumber != null && maxAgentsNumber > -1)

  return (
    <div>
      {showAgentsToolbar ? (
        <AgentPoolTabToolbar>
          {showAssignAgentsButton ? (
            <AssignAgentsButton
              className={styles.assignAgentsButton}
              agentPoolId={agentPoolId}
              agentPoolName={agentPoolTree.title}
              agentsNumber={agentsNumber}
              maxAgentsNumber={maxAgentsNumber}
            />
          ) : isEmpty ? (
            <div>{'No assigned agents'}</div>
          ) : null}
          {maxAgentsNumber != null && maxAgentsNumber > -1 ? (
            agentsNumber < maxAgentsNumber ? (
              <div>{`Maximum limit of agents is ${maxAgentsNumber}`}</div>
            ) : (
              <div
                className={styles.warning}
              >{`Maximum limit of ${maxAgentsNumber} agents is reached`}</div>
            )
          ) : null}
        </AgentPoolTabToolbar>
      ) : null}

      {agentPoolTree.children.length > 0 ? (
        <BuildLineLayout showAgent={false} showExactDuration={false} showPath showBranch>
          {cloudAgentsNumber > 1 || standAloneAgentsNumber > 1 ? (
            <AgentsSortingSelect
              className={styles.sortingSelect}
              onChange={setAgentsSorting}
              selected={agentsSorting}
            />
          ) : null}
          {cloudAgentsNumber > 0 ? (
            <>
              {standAloneAgentsNumber > 0 ? (
                <H2 className={styles.heading}>{`${cloudAgentsNumber} ${plur(
                  'cloud image',
                  cloudAgentsNumber,
                )}`}</H2>
              ) : null}

              {sortSameChildren(cloudAgentNodes).map(node => (
                <CloudAgentRow
                  className={styles.cloudAgent}
                  key={node.id}
                  removable={canRemoveAgents}
                  {...node}
                />
              ))}
            </>
          ) : null}
          {standAloneAgentsNumber > 0 ? (
            <>
              {cloudAgentsNumber > 0 ? (
                <H2 className={styles.heading}>{`${standAloneAgentsNumber} ${plur(
                  'agent',
                  standAloneAgentsNumber,
                )}`}</H2>
              ) : null}
              <AgentsGroup
                childNodes={standAloneAgentNodes}
                sortingType={agentsSorting?.value}
                canRemoveAgents={canRemoveAgents}
              />
            </>
          ) : null}
        </BuildLineLayout>
      ) : null}
    </div>
  )
}

export default AgentsTab
